import { useCallback, useEffect, useMemo, useState } from 'react'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'

import {
  approveRevenueRecommendation,
  dismissRevenueRecommendation,
  getRevenueIntelligenceInsights,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import {
  buildRecommendationCardsForInsights,
  formatMoney,
} from '../../utilities/recommendationCards'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { RecommendationCard } from '../RecommendationCard'
import { ReportRefreshButton } from '../ReportRefreshButton'

import './insightsDashboardWrapper.scss'

const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'operations', label: 'Operations' },
]

const PRIORITY_OPTIONS = [
  { value: '', label: 'All priorities' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'executed', label: 'Executed' },
]

const INSIGHT_TYPE_OPTIONS = [
  { value: '', label: 'All insight types' },
  { value: 'weekday_underperformance', label: 'Weekday underperformance' },
  { value: 'sales_pace', label: 'Sales pace' },
]

function SummaryStat({ label, value, accent }) {
  return (
    <Card
      body
      className={`card--xs card-with-border insights-summary-stat ${accent || ''}`}
    >
      <span className="insights-summary-stat__label">{label}</span>
      <span className="insights-summary-stat__value">{value}</span>
    </Card>
  )
}

export default function InsightsDashboardWrapper({ organization }) {
  const organizationUUID = organization?.uuid
  const [insights, setInsights] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const { refreshToken, refresh, completeRefresh } = useReportRefresh(isLoading)
  const [error, setError] = useState(null)
  const [actionMessage, setActionMessage] = useState(null)
  const [actionLoadingId, setActionLoadingId] = useState(null)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('pending')
  const [insightTypeFilter, setInsightTypeFilter] = useState('')

  const loadInsights = useCallback(async () => {
    if (!organizationUUID) return
    setIsLoading(true)
    setError(null)
    try {
      const res = await getRevenueIntelligenceInsights(organizationUUID, {
        limit: 100,
      })
      setInsights(Array.isArray(res?.data?.data) ? res.data.data : [])
    } catch (err) {
      console.error('Failed to load insights dashboard data', err)
      setInsights([])
      setError('Could not load insights. Try refreshing the page.')
    } finally {
      setIsLoading(false)
      completeRefresh()
    }
  }, [organizationUUID])

  useEffect(() => {
    loadInsights()
  }, [loadInsights, refreshToken])

  const allCards = useMemo(
    () => buildRecommendationCardsForInsights(insights),
    [insights]
  )

  const filteredCards = useMemo(() => {
    return allCards.filter((card) => {
      if (categoryFilter && card.category !== categoryFilter) return false
      if (priorityFilter && card.priority !== priorityFilter) return false
      if (statusFilter && card.status !== statusFilter) return false
      if (insightTypeFilter && card.insightType !== insightTypeFilter)
        return false
      return true
    })
  }, [
    allCards,
    categoryFilter,
    priorityFilter,
    statusFilter,
    insightTypeFilter,
  ])

  const summary = useMemo(() => {
    const pending = allCards.filter((c) => c.status === 'pending')
    const highPriority = pending.filter((c) => c.priority === 'high')
    const totalLift = pending.reduce(
      (sum, c) => sum + Number(c.expectedRevenueLift || 0),
      0
    )

    return {
      insightCount: insights.length,
      cardCount: allCards.length,
      pendingCount: pending.length,
      highPriorityCount: highPriority.length,
      totalLift,
    }
  }, [allCards, insights.length])

  const handlePrimaryAction = (card) => {
    setActionMessage(
      `Action queued: ${card.ctaLabel} (${card.actionPayload?.actionType || 'review'})`
    )
  }

  const handleRecommendationStatus = async (card, action) => {
    const recommendationId = card.recommendationId
    if (!organizationUUID || !recommendationId) {
      setActionMessage(
        'This recommendation is preview-only until saved insights include an ID.'
      )
      return
    }

    setActionLoadingId(card.id)
    setActionMessage(null)
    try {
      if (action === 'approve') {
        await approveRevenueRecommendation(organizationUUID, recommendationId)
        setActionMessage(`Approved: ${card.title}`)
      } else {
        await dismissRevenueRecommendation(organizationUUID, recommendationId)
        setActionMessage(`Dismissed: ${card.title}`)
      }
      await loadInsights()
    } catch (err) {
      console.error(`Failed to ${action} recommendation`, err)
      setError(`Could not ${action} this recommendation. Try again.`)
    } finally {
      setActionLoadingId(null)
    }
  }

  return (
    <section
      className="spacer-sm insights-dashboard-wrapper"
      id="insights-dashboard"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Insights dashboard</h1>
        </div>
        <p className="section-header-desc">
          Actionable recommendations generated from revenue intelligence. Each
          insight type produces reusable recommendation cards you can review,
          approve, or dismiss.
        </p>
      </header>

      <Row className="g-3 mb-3">
        <Col sm={6} lg={3}>
          <SummaryStat label="Active insights" value={summary.insightCount} />
        </Col>
        <Col sm={6} lg={3}>
          <SummaryStat
            label="Pending recommendations"
            value={summary.pendingCount}
            accent="accent-warning"
          />
        </Col>
        <Col sm={6} lg={3}>
          <SummaryStat
            label="High priority"
            value={summary.highPriorityCount}
            accent="accent-danger"
          />
        </Col>
        <Col sm={6} lg={3}>
          <SummaryStat
            label="Est. pending lift"
            value={formatMoney(summary.totalLift, { signed: true })}
            accent="accent-success"
          />
        </Col>
      </Row>

      <Card
        body
        className="card--xs card-with-border insights-dashboard-filters mb-3"
      >
        <Row className="g-3 align-items-end">
          <Col md={3}>
            <Form.Group controlId="insights-filter-type">
              <Form.Label>Insight type</Form.Label>
              <Form.Select
                value={insightTypeFilter}
                onChange={(e) => setInsightTypeFilter(e.target.value)}
              >
                {INSIGHT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="insights-filter-category">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group controlId="insights-filter-priority">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group controlId="insights-filter-status">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <ReportRefreshButton
              onRefresh={refresh}
              isRefreshing={isLoading}
              disabled={isLoading}
            />
          </Col>
        </Row>
      </Card>

      {error && (
        <Card body className="card--xs card-with-border mb-3">
          <p className="text-danger mb-0">{error}</p>
        </Card>
      )}

      {actionMessage && (
        <Card body className="card--xs card-with-border mb-3">
          <p className="text-success mb-0">{actionMessage}</p>
        </Card>
      )}

      {isLoading && <PageLoadingContainer />}

      {!isLoading && filteredCards.length === 0 && (
        <Card body className="card--xs card-with-border">
          <Stack gap={2}>
            <p className="mb-0 text-muted">
              No recommendation cards match your filters.
            </p>
            <p className="mb-0 small text-muted">
              Weekday underperformance insights are generated by the daily
              benchmark job. Sales pace insights can be created from{' '}
              <a href="/revenue-insights">Revenue insights</a>.
            </p>
          </Stack>
        </Card>
      )}

      {!isLoading && filteredCards.length > 0 && (
        <>
          <Stack
            direction="horizontal"
            className="justify-content-between mb-2"
          >
            <h2 className="h5 mb-0">Recommendations</h2>
            <Badge bg="light" text="dark">
              {filteredCards.length} card{filteredCards.length === 1 ? '' : 's'}
            </Badge>
          </Stack>
          <Row className="g-3">
            {filteredCards.map((card) => (
              <Col key={card.id} md={6} xl={4}>
                <RecommendationCard
                  card={card}
                  onPrimaryAction={handlePrimaryAction}
                  onApprove={(c) => handleRecommendationStatus(c, 'approve')}
                  onDismiss={(c) => handleRecommendationStatus(c, 'dismiss')}
                  isActionLoading={actionLoadingId === card.id}
                />
              </Col>
            ))}
          </Row>
        </>
      )}
    </section>
  )
}
